import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ScoreDisplay } from "./components/score-display";

import { Results, Category } from "./state";

interface ResultsPanelProps {
  results: Results;
  isInPopup?: boolean;
}

// New component for category scores
interface CategoryScoreProps {
  category: Category;
  index: number;
  showExplanation: boolean;
}

function CategoryScore({ category, index, showExplanation }: CategoryScoreProps) {
  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
              index === 0
                ? "bg-blue-100"
                : index === 1
                  ? "bg-green-100"
                  : index === 2
                    ? "bg-purple-100"
                    : "bg-amber-100"
            }`}
          >
            <span
              className={`font-medium ${
                index === 0
                  ? "text-blue-600"
                  : index === 1
                    ? "text-green-600"
                    : index === 2
                      ? "text-purple-600"
                      : "text-amber-600"
              }`}
            >
              {index + 1}
            </span>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{category.name}</h3>
              <Badge
                variant={
                  category.score >= 80 ? "default" : category.score >= 60 ? "secondary" : "outline"
                }
              >
                {category.score.toFixed(0)}/100
              </Badge>
            </div>
            <Progress
              value={category.score}
              className={`mb-2 h-2 ${
                category.score >= 80
                  ? "[&>div]:bg-green-600"
                  : category.score >= 60
                    ? "[&>div]:bg-yellow-600"
                    : "[&>div]:bg-red-600"
              }`}
            />
          </div>
        </div>
      </div>

      {showExplanation && (
        <div className="mt-2 space-y-3">
          <p className="text-sm text-gray-500">{category.explanation}</p>

          {category.subCategories && category.subCategories.length > 0 && (
            <div className="space-y-3 pl-4">
              {category.subCategories.map((subcategory, subIndex) => (
                <div key={subIndex} className="border-l-2 border-gray-200 pl-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">{subcategory.name}</h4>
                    <Badge
                      variant={
                        subcategory.score >= 80
                          ? "default"
                          : subcategory.score >= 60
                            ? "secondary"
                            : "outline"
                      }
                      className="text-xs"
                    >
                      {subcategory.score.toFixed(0)}/100
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{subcategory.explanation}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ResultsPanel({ results, isInPopup = false }: ResultsPanelProps) {
  const { t } = useTranslation();
  const [showExplanation, setShowExplanation] = useState(false);

  return (
    <div className={`space-y-6 ${isInPopup ? "max-h-[80vh] overflow-y-auto" : ""}`}>
      <div className="space-y-4">
        <ScoreDisplay
          score={results.overallScore}
          label={t("thumbnailReviewer.results.overallScore")}
          className="mt-2"
        />
      </div>

      <Separator />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">{t("thumbnailReviewer.results.categoryScores")}</h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-gray-500"
            onClick={() => setShowExplanation(!showExplanation)}
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        </div>

        {results.categories.map((category, index) => (
          <div key={index}>
            <CategoryScore category={category} index={index} showExplanation={showExplanation} />
            {index < results.categories.length - 1 && <Separator className="my-4" />}
          </div>
        ))}
      </div>
    </div>
  );
}
